import { useState } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { isPlaceholderFile } from '@backend/lib/db/typeRoots/fileWithUrl'
import { WorkspaceMediaPreview } from './workspace-media-preview'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'
import { GeneratingMediaPreview, type GeneratingImage } from './generating-media-preview'
export type { GeneratingImage }
import { canAddMultipleMedia } from '@/lib/workspace-media-validation'
import { getWorkspaceMediaType } from './util'
import { AddImageIcon } from '@/components/ui/icons/icons-new'
import { Card } from '@/components/ui/shadcn/card'
import { convertPdfToImages } from '@/lib/pdf-to-images'
import { uploadToS3 } from '@/lib/utils'
import { captureError } from '@/lib/errors'

const ALLOWED_TYPES = {
  image: ['image/jpeg', 'image/jpg', 'image/png'],
  video: ['video/mp4', 'video/mov'],
  document: ['application/pdf'],
}

interface Props {
  media: WorkspaceMedia[]
  setMedia: (media: WorkspaceMedia[], updateDb: boolean) => Promise<void>
  workspaceId: number
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onEdit?: (media: WorkspaceMedia, index: number) => void
  onAddMedia?: () => void
  onAutoDesign?: (media: WorkspaceMedia, index: number) => void
  generatingImages?: GeneratingImage[]
  /** Draw the AI label over image media in the workspace. Opt-in. */
  showAiLabel?: boolean
  aiLabelStyle?: AiLabelStyle
}

export function WorkspaceMediaManager({
  media,
  setMedia,
  workspaceId,
  fileInputRef,
  onEdit,
  onAddMedia,
  onAutoDesign,
  generatingImages = [],
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  const [uploadingCount, setUploadingCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [convertingPdf, setConvertingPdf] = useState<{
    fileName: string
    totalPages?: number
  } | null>(null)

  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: deleteAssets } = trpc.asset.delete.useMutation()

  const handleFileInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return

    try {
      const filesToUpload: Array<{ source: 'local'; url: string; file: File }> = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file) continue

        if (file.type === 'application/pdf') {
          try {
            setConvertingPdf({ fileName: file.name })
            const { imageFiles, totalPages } = await convertPdfToImages(file, 9)
            setConvertingPdf(null)

            if (imageFiles.length === 0) {
              toast.error(`PDF "${file.name}" has no pages or conversion failed`)
              continue
            }
            if (totalPages > 9) {
              toast.warning(
                `PDF has ${totalPages} pages. Only the first 9 pages were converted (LinkedIn limit).`,
                {
                  duration: 5000,
                },
              )
            }

            for (const imageFile of imageFiles) {
              filesToUpload.push({
                source: 'local',
                url: URL.createObjectURL(imageFile),
                file: imageFile,
              })
            }
          } catch (error) {
            captureError(error, {
              flow: 'workspace-media.pdfConvert',
              workspaceId,
              fileName: file.name,
              fileSize: file.size,
            })
            setConvertingPdf(null)
            toast.error(`Failed to convert PDF "${file.name}"`)
            continue
          }
        } else {
          const mediaType = getWorkspaceMediaType(file.type)
          const allowedTypes = ALLOWED_TYPES[mediaType as keyof typeof ALLOWED_TYPES]

          if (!allowedTypes || !allowedTypes.includes(file.type)) {
            toast.error(`File "${file.name}" is not supported`, {
              description: 'Only JPEG, PNG, MP4, MOV, and PDF files are supported',
            })
            continue
          }

          filesToUpload.push({
            source: 'local',
            url: URL.createObjectURL(file),
            file,
          })
        }
      }

      const mediaTypes = filesToUpload.map((f) => getWorkspaceMediaType(f.file.type))
      const validation = canAddMultipleMedia(media, mediaTypes)

      if (!validation.allowed) {
        toast.error(validation.reason)
        event.target.value = ''
        return
      }

      setUploadingCount(filesToUpload.length)

      const newMedia = await Promise.all(
        filesToUpload.map(async (fileData) => {
          const { key, uploadUrl, assetUrl } = await getUploadUrl({
            type: 'workspace',
            workspaceId: workspaceId,
          })
          await uploadToS3(uploadUrl, fileData.file)

          return {
            type: getWorkspaceMediaType(fileData.file.type),
            file: {
              source: 'remote',
              url: assetUrl,
              key,
              file: {
                name: fileData.file.name,
                type: fileData.file.type,
                size: fileData.file.size,
              },
            },
          } as WorkspaceMedia
        }),
      )

      setUploadingCount(0)
      setConvertingPdf(null)
      await setMedia([...media, ...newMedia], true)
      event.target.value = ''
    } catch (error) {
      captureError(error, {
        flow: 'workspace-media.handleFileInput',
        workspaceId,
        fileCount: files.length,
        totalBytes: Array.from(files).reduce((sum, f) => sum + f.size, 0),
      })
      const errorMessage = error instanceof Error ? error.message : String(error)
      const cleanErrorMessage = errorMessage.replace(/^TRPCClientError:\s*/i, '')
      toast.error('Failed to process files', {
        description: cleanErrorMessage,
        duration: 7000,
      })
      setUploadingCount(0)
      setConvertingPdf(null)
    }
  }

  const removeFile = async (index: number) => {
    await setMedia(
      media.filter((_, i) => i !== index),
      true,
    )
    const m = media[index]
    if (m && m.file.source === 'remote' && !isPlaceholderFile(m.file)) {
      await deleteAssets({ keys: [m.file.key] })
    }
  }

  const duplicateFile = async (index: number) => {
    const mediaToDuplicate = media[index]
    if (!mediaToDuplicate) return

    // Deep copy to avoid shared references
    let duplicatedMedia: WorkspaceMedia

    // For remote files, we need to create a new upload so the duplicate has its own key
    // This prevents deletion of one item from affecting the other
    let duplicatedFile: WorkspaceMedia['file']

    if (mediaToDuplicate.file.source === 'remote' && !isPlaceholderFile(mediaToDuplicate.file)) {
      // Fetch the remote file and re-upload it with a new key
      try {
        const response = await fetch(mediaToDuplicate.file.url)
        const blob = await response.blob()
        const file = new File([blob], mediaToDuplicate.file.file.name, {
          type: mediaToDuplicate.file.file.type,
        })

        const { key, uploadUrl, assetUrl } = await getUploadUrl({
          type: 'workspace',
          workspaceId: workspaceId,
        })
        await uploadToS3(uploadUrl, file)

        duplicatedFile = {
          source: 'remote' as const,
          url: assetUrl,
          key,
          file: {
            name: mediaToDuplicate.file.file.name,
            type: mediaToDuplicate.file.file.type,
            size: mediaToDuplicate.file.file.size,
          },
        }
      } catch (error) {
        captureError(error, {
          flow: 'workspace-media.duplicate',
          workspaceId,
          fileName: mediaToDuplicate.file.file.name,
          fileSize: mediaToDuplicate.file.file.size,
          fileType: mediaToDuplicate.file.file.type,
        })
        const errorMessage = error instanceof Error ? error.message : String(error)
        const cleanErrorMessage = errorMessage.replace(/^TRPCClientError:\s*/i, '')
        toast.error('Failed to duplicate media', {
          description: cleanErrorMessage,
          duration: 7000,
        })
        return
      }
    } else {
      return
    }

    if (mediaToDuplicate.type === 'canvas') {
      duplicatedMedia = {
        type: 'canvas',
        nodes: JSON.parse(JSON.stringify(mediaToDuplicate.nodes)),
        file: duplicatedFile,
        aspectRatio: mediaToDuplicate.aspectRatio,
        origin: mediaToDuplicate.origin,
        hasSyntheticEdits: mediaToDuplicate.hasSyntheticEdits,
      }
    } else {
      duplicatedMedia = {
        type: mediaToDuplicate.type,
        file: duplicatedFile,
        origin: mediaToDuplicate.origin,
        hasSyntheticEdits: mediaToDuplicate.hasSyntheticEdits,
      }
    }

    const newMedia = [...media]
    newMedia.splice(index + 1, 0, duplicatedMedia)
    await setMedia(newMedia, true)
  }

  const reorderFiles = async (fromIndex: number, toIndex: number) => {
    const newFiles = [...media]
    const [removed] = newFiles.splice(fromIndex, 1)
    newFiles.splice(toIndex, 0, removed!)
    await setMedia(newFiles, true)
  }

  const isFileDrag = (e: React.DragEvent): boolean => {
    return Array.from(e.dataTransfer.types).includes('Files')
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    if (!isFileDrag(e)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0 && fileInputRef.current) {
      const dataTransfer = new DataTransfer()
      Array.from(files).forEach((file) => dataTransfer.items.add(file))
      fileInputRef.current.files = dataTransfer.files

      const event = new Event('change', { bubbles: true })
      fileInputRef.current.dispatchEvent(event)
    }
  }

  const gridLayout = (
    <div
      className="flex flex-1 flex-col gap-2 overflow-auto"
      onDragOver={uploadingCount > 0 ? undefined : handleDragOver}
      onDragLeave={uploadingCount > 0 ? undefined : handleDragLeave}
      onDrop={uploadingCount > 0 ? undefined : handleDrop}>
      <div className="grid grid-cols-2 gap-2">
        {media.map((mediaItem, index) => {
          const key =
            mediaItem.type === 'canvas'
              ? `${JSON.stringify(mediaItem.nodes)}-${index}`
              : `${mediaItem.file.url}-${index}`
          return (
            <WorkspaceMediaPreview
              key={key}
              media={mediaItem}
              index={index}
              onRemove={removeFile}
              onReorder={reorderFiles}
              onEdit={() => onEdit?.(mediaItem, index)}
              onDuplicate={duplicateFile}
              onAutoDesign={onAutoDesign ? (media, idx) => onAutoDesign(media, idx) : undefined}
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
          )
        })}

        {generatingImages.map((generating) => (
          <GeneratingMediaPreview key={`generating-${generating.id}`} generating={generating} />
        ))}

        {/* PDF Conversion Status */}
        {convertingPdf && (
          <Card className="relative flex aspect-square items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="border-border border-t-primary h-8 w-8 animate-spin rounded-full border-4"></div>
              <span className="px-2 text-center text-xs">Converting PDF...</span>
              <span className="text-muted-foreground w-full truncate px-2 text-center text-xs">
                {convertingPdf.fileName}
              </span>
            </div>
          </Card>
        )}

        {/* Uploading Status */}
        {Array.from({ length: uploadingCount }).map((_, index) => (
          <Card
            key={`uploading-${index}`}
            className="relative flex aspect-square items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="border-border border-t-primary h-8 w-8 animate-spin rounded-full border-4"></div>
              <span className="text-xs">Uploading...</span>
            </div>
          </Card>
        ))}

        {/* Add Media Square */}
        <Card
          onClick={uploadingCount > 0 ? undefined : () => onAddMedia?.()}
          onDragOver={uploadingCount > 0 ? undefined : handleDragOver}
          onDragLeave={uploadingCount > 0 ? undefined : handleDragLeave}
          onDrop={uploadingCount > 0 ? undefined : handleDrop}
          className={`relative flex aspect-square items-center justify-center ${
            uploadingCount > 0
              ? 'cursor-not-allowed opacity-50'
              : isDragging
                ? 'bg-hover border-primary border-2'
                : 'hover:bg-hover cursor-pointer'
          }`}>
          <div className="flex flex-col items-center gap-2">
            <AddImageIcon width={24} height={24} color="var(--foreground)" />
            <span className="text-sm font-medium">
              {uploadingCount > 0 ? 'Uploading...' : isDragging ? 'Drop files here' : 'Add media'}
            </span>
          </div>
        </Card>
      </div>
    </div>
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,video/mp4,video/mov,application/pdf"
        onChange={handleFileInput}
        className="hidden"
      />

      <div className="flex h-full w-full flex-col">
        {media.length === 0 &&
        uploadingCount === 0 &&
        !convertingPdf &&
        generatingImages.length === 0 ? (
          <Card
            onClick={uploadingCount > 0 ? undefined : () => onAddMedia?.()}
            onDragOver={uploadingCount > 0 ? undefined : handleDragOver}
            onDragLeave={uploadingCount > 0 ? undefined : handleDragLeave}
            onDrop={uploadingCount > 0 ? undefined : handleDrop}
            className={`hover:bg-hover flex min-h-[200px] items-center justify-center ${
              uploadingCount > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}>
            <div className="flex flex-col items-center gap-2 text-center">
              <AddImageIcon width={24} height={24} color="var(--foreground)" />
              <p className="text-sm">
                {uploadingCount > 0
                  ? 'Uploading...'
                  : isDragging
                    ? 'Drop files here'
                    : 'Drag and drop files, click to browse or generate images'}
              </p>
              <p className="text-muted-foreground text-xs">JPEG, PNG, MP4, MOV, PDF</p>
            </div>
          </Card>
        ) : (
          gridLayout
        )}
      </div>
    </>
  )
}
